import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/Photos.css';
import { updatePageSEO, seoData } from '../utils/SeoUtils';

function Photos() {
  const [selectedImage, setSelectedImage] = useState(null);
  // Add refs for swipe gesture
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update SEO for photos page
    updatePageSEO(seoData.photos.title, seoData.photos.description, seoData.photos.keywords);
  }, []);

  // Dynamically import all images from the portfolio folder
  const importAll = (r) => {
    const images = {};
    r.keys().map((item, index) => {
      return (images[item.replace('./', '')] = r(item));
    });
    return images;
  };

  // Natural sort function for proper numeric ordering
  const naturalSort = (a, b) => {
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Get all images from the portfolio folder
  const importedImages = importAll(
    require.context('/public/images/portifolio', false, /\.(png|jpe?g|svg)$/)
  );

  // Convert to array of filenames with proper numeric sorting
  const images = Object.keys(importedImages).sort(naturalSort);

  const openModal = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const nextImage = useCallback(() => {
    const currentIndex = images.indexOf(selectedImage.split('/').pop());
    const nextIndex = (currentIndex + 1) % images.length;
    setSelectedImage(`/images/portifolio/${images[nextIndex]}`);
  }, [images, selectedImage]);

  const prevImage = useCallback(() => {
    const currentIndex = images.indexOf(selectedImage.split('/').pop());
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(`/images/portifolio/${images[prevIndex]}`);
  }, [images, selectedImage]);

  // Keyboard navigation
  useEffect(() => {
    if (selectedImage) {
      const handleKeyDown = (e) => {
        if (e.key === 'ArrowRight') {
          nextImage();
        } else if (e.key === 'ArrowLeft') {
          prevImage();
        } else if (e.key === 'Escape') {
          closeModal();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, nextImage, prevImage]);

  // Swipe handlers for modal
  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchEndX.current = e.touches[0].clientX;
    }
  };
  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const deltaX = touchEndX.current - touchStartX.current;
      if (Math.abs(deltaX) > 50) {
        if (deltaX < 0) {
          nextImage(); // swipe left
        } else {
          prevImage(); // swipe right
        }
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className="photos">
      <section className="photos-description">
        <div className="container">
          <h2>Portfólio de Trabalhos Profissionais</h2>
          <p>
            Nossa galeria apresenta a ampla gama de projetos que concluímos usando nossos muncks e
            guindastes. De mudanças residenciais a grandes projetos de construção, instalações
            industriais a recuperações emergenciais, temos a experiência e equipamentos para lidar
            com qualquer desafio.
          </p>
          <div className="work-categories">
            <div className="category">
              <h4>Projetos de Construção</h4>
              <p>
                Posicionamento de materiais, instalação de vigas de aço e posicionamento de
                equipamentos pesados
              </p>
            </div>
            <div className="category">
              <h4>Serviços Residenciais</h4>
              <p>
                Mudança de móveis, instalação de eletrodomésticos e suporte para construção
                residencial
              </p>
            </div>
            <div className="category">
              <h4>Operações Industriais</h4>
              <p>
                Instalação de equipamentos de fábrica, suporte de manutenção e realocações
                industriais
              </p>
            </div>
            <div className="category">
              <h4>Serviços de Emergência</h4>
              <p>
                Operações de recuperação, limpeza de acidentes e remoção urgente de equipamentos
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="photos-grid">
        <div className="container">
          <div className="gallery">
            {images.map((image, index) => (
              <div
                key={index}
                className="gallery-item"
                onClick={() => openModal(`/images/portifolio/${image}`)}
                onKeyDown={(e) => e.key === 'Enter' && openModal(`/images/portifolio/${image}`)}
                role="button"
                tabIndex={0}
                aria-label={`Ver projeto ${index + 1} em tamanho original`}
              >
                <img
                  src={`/images/portifolio/${image}`}
                  alt={`Projeto ${index + 1} em Rondonópolis, Roo - Munck e Guindaste`}
                  loading="lazy"
                  decoding="async"
                />
                <div className="overlay">
                  <span>Ver Tamanho Original</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedImage && (
        <div
          className="modal"
          onClick={closeModal}
          onKeyDown={(e) => e.key === 'Escape' && closeModal()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role="button"
          aria-label="Close modal"
          tabIndex={0}
        >
          <button
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            aria-label="Fechar modal"
          >
            &times;
          </button>
          <button
            className="nav-button prev"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
          >
            &#8249;
          </button>
          <button
            className="nav-button next"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
          >
            &#8250;
          </button>
          <div className="modal-content">
            <img src={selectedImage} alt="Projeto selecionado" loading="lazy" decoding="async" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Photos;
